import { Copy, Eye, EyeOff, Search, X } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  useClusterPhrasesQuery,
  useTogglePhraseHiddenMutation,
} from "@/entities/projects/queries";
import type { ClusterOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Sparkline } from "@/widgets/Sparkline";
import { TempBadge } from "@/widgets/TempBadge";

const PAGE_SIZE = 100;

export interface ClusterDrawerProps {
  projectId: string;
  cluster: ClusterOut | null;
  onClose: () => void;
}

export function ClusterDrawer({
  projectId,
  cluster,
  onClose,
}: ClusterDrawerProps): JSX.Element {
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<"count_desc" | "count_asc">("count_desc");
  const [page, setPage] = React.useState(0);
  const [showHidden, setShowHidden] = React.useState(false);

  React.useEffect(() => {
    setQuery("");
    setPage(0);
    setShowHidden(false);
  }, [cluster?.id]);

  const open = cluster !== null;
  const phrasesQ = useClusterPhrasesQuery(
    projectId,
    cluster?.id ?? "",
    {
      q: query,
      sort,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      show_hidden: showHidden,
    },
    open,
  );

  const toggleHiddenM = useTogglePhraseHiddenMutation(projectId, cluster?.id ?? "");

  const copyAll = async (): Promise<void> => {
    if (!cluster) return;
    try {
      const mod = await import("@/entities/projects/api");
      const data = await mod.getClusterPhrases(projectId, cluster.id, {
        sort: "count_desc",
        limit: 500,
        offset: 0,
        show_hidden: showHidden,
      });
      const text = data.items.map((p) => p.phrase).join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(`Скопировано ${data.items.length} фраз`);
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full border-l border-white/10 bg-background p-0 sm:max-w-[520px]"
      >
        {cluster && (
          <div className="flex h-full flex-col">
            <SheetHeader className="border-b border-white/10 px-5 py-4">
              <SheetTitle className="text-base font-bold text-white">
                {cluster.main_phrase}
              </SheetTitle>
              <div className="flex flex-wrap items-center gap-2 pt-2 text-xs text-muted-foreground">
                <TempBadge
                  heat={{
                    temperature: cluster.temperature,
                    trend_pct: cluster.trend_pct,
                  }}
                />
                <span>·</span>
                <span className="font-mono text-cyan">
                  {formatNumber(cluster.total_count)}
                </span>
                <span>запросов в кластере · {cluster.size} фраз</span>
              </div>
              {cluster.sparkline && cluster.sparkline.length > 0 && (
                <div className="pt-1 text-cyan">
                  <Sparkline data={cluster.sparkline} width={200} height={36} />
                </div>
              )}
            </SheetHeader>

            <div className="space-y-2 border-b border-white/10 px-5 py-3">
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Поиск по фразам…"
                  className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <button
                  type="button"
                  className="hover:text-white"
                  onClick={() =>
                    setSort((s) =>
                      s === "count_desc" ? "count_asc" : "count_desc",
                    )
                  }
                >
                  Сортировка: {sort === "count_desc" ? "по убыванию" : "по возрастанию"} ⇅
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHidden((v) => !v);
                      setPage(0);
                    }}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 hover:bg-white/[0.04]",
                      showHidden && "border-purple/45 text-purple",
                    )}
                    title={
                      showHidden ? "Показывать только видимые" : "Показать скрытые"
                    }
                  >
                    {showHidden ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                    {showHidden ? "скрытые видны" : "скрытые"}
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyAll}
                    className="border-white/15 bg-white/[0.04]"
                  >
                    <Copy className="h-3.5 w-3.5" /> Копировать все
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3">
              {phrasesQ.isLoading ? (
                <div className="text-sm text-muted-foreground">Загрузка…</div>
              ) : phrasesQ.isError ? (
                <div className="text-sm text-pink">Не удалось загрузить</div>
              ) : phrasesQ.data && phrasesQ.data.items.length > 0 ? (
                <>
                  <div className="space-y-1.5">
                    {phrasesQ.data.items.map((p) => (
                      <PhraseRow
                        key={p.id}
                        phrase={p.phrase}
                        countBase={p.count_base}
                        countExact={p.count_exact}
                        isHidden={p.is_hidden}
                        onToggleHidden={() =>
                          toggleHiddenM.mutate({
                            phraseId: p.id,
                            body: { is_hidden: !p.is_hidden },
                          })
                        }
                      />
                    ))}
                  </div>
                  {phrasesQ.data.total > (page + 1) * PAGE_SIZE && (
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      className="mt-3 w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 text-sm text-foreground hover:bg-white/[0.06]"
                    >
                      Показать ещё (всего {phrasesQ.data.total})
                    </button>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Ничего не найдено
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PhraseRow({
  phrase,
  countBase,
  countExact,
  isHidden,
  onToggleHidden,
}: {
  phrase: string;
  countBase: number;
  countExact: number | null;
  isHidden: boolean;
  onToggleHidden: () => void;
}): JSX.Element {
  return (
    <div
      className={cn(
        "group flex items-baseline justify-between gap-3 border-b border-white/[0.04] py-1 text-sm",
        isHidden && "opacity-50",
      )}
    >
      <span className="break-words text-foreground/90">{phrase}</span>
      <div className="flex shrink-0 items-baseline gap-2">
        <span
          className="font-mono text-xs text-muted-foreground"
          title={
            countExact !== null
              ? `точная (в кавычках): ${countExact.toLocaleString("ru-RU")}`
              : "точная частота недоступна"
          }
        >
          {formatNumber(countBase)}
        </span>
        <button
          type="button"
          onClick={onToggleHidden}
          aria-label={isHidden ? "Показать" : "Скрыть как нерелевантное"}
          title={isHidden ? "Показать" : "Скрыть как нерелевантное"}
          className={cn(
            "rounded p-1 text-muted-foreground transition opacity-0 group-hover:opacity-100",
            "hover:bg-white/[0.06] hover:text-pink",
            isHidden && "opacity-100 text-pink",
          )}
        >
          {isHidden ? <Eye className="h-3 w-3" /> : <X className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
